import React, { useState, useRef, useEffect } from 'react'
import BaseModal from '@components/common/BaseModal/BaseModal.component'
import InputField from '@components/common/InputField/InputField.component'
import Button from '@components/common/Button/Button.component'
import './CreateFolderModal.css'

interface CreateFolderModalProps {
  show: boolean
  onHide: () => void
  onCreate: (folderName: string) => void
  isLoading?: boolean
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  show,
  onHide,
  onCreate,
  isLoading = false,
}) => {
  const [folderName, setFolderName] = useState('')
  const [isValid, setIsValid] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus()
    }
  }, [show])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    setFolderName(value)
    setIsValid(value.trim().length > 0)
  }

  const handleCancel = (): void => {
    setFolderName('')
    setIsValid(false)
    onHide()
  }

  const handleCreate = (): void => {
    if (isValid && folderName.trim()) {
      onCreate(folderName.trim())
      handleCancel()
    }
  }

  const modalContent = (
    <div className="create-folder-form">
      <InputField
        label="Folder name"
        type="text"
        value={folderName}
        onChange={handleInputChange}
        placeholder="Enter folder name"
        disabled={isLoading}
        ref={inputRef}
      />
    </div>
  )

  const modalFooter = (
    <div className="create-folder-actions">
      <Button
        className="create-folder-cancel"
        onClick={handleCancel}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Button
        className="create-folder-create"
        onClick={handleCreate}
        disabled={!isValid || isLoading}
      >
        {isLoading ? 'Creating...' : 'Create'}
      </Button>
    </div>
  )

  return (
    <BaseModal
      show={show}
      handleClose={handleCancel}
      title="Create folder"
      footer={modalFooter}
      className="create-folder-modal"
      size="sm"
    >
      {modalContent}
    </BaseModal>
  )
}

export default CreateFolderModal
